import { useGetContentListQuery } from '@api/api';
import { ContentRequest, EpiBlock, EpiContent } from '@api/types';
import { BlockWrapper, DataLoadingProgress, GenericError } from '@components';
import { skipToken } from '@reduxjs/toolkit/query';
import { isValidElement } from 'react';

export type ContentAreaProps = {
  content?: EpiContent;
  ids?: number[] | string[];
  isCatalogContent?: boolean;
  children?: React.ReactNode;
  loadingComponent?: React.ReactNode;
  wrapperComponent?: React.FC<any>;
  epiEditMode?: boolean;
  enableContextMenu?: boolean;
  enableNativeOverlay?: boolean;
  disableEditPanel?: boolean;
};

export const ContentArea = ({
  content,
  ids,
  isCatalogContent,
  children,
  loadingComponent,
  epiEditMode,
  ...props
}: ContentAreaProps) => {
  const isReactNode = content !== null && isValidElement(content);
  const blocks: EpiBlock[] = Array.isArray(content) ? content : [];

  const shouldLoad = ids || (!epiEditMode && blocks.some((x) => !x?.contentLink?.expanded));

  const references = ids
    ? ids
    : blocks.map((x) => x?.contentLink?.id).filter((id): id is number => typeof id === 'number');

  const mappedReferences = isCatalogContent
    ? references.map((id) => `${id}__CatalogContent`)
    : references;

  const request: ContentRequest = {
    references: mappedReferences,
    epiEditMode,
    expand: '*',
  };

  const { data, isLoading, isError } = useGetContentListQuery(shouldLoad ? request : skipToken);

  const blocksToRender = shouldLoad ? data ?? [] : blocks;

  if (isReactNode) return content;
  if (isLoading) return <>{loadingComponent || <DataLoadingProgress />}</>;
  if (isError) return <GenericError />;

  return (
    <>
      {children}
      {Array.isArray(blocksToRender) &&
        blocksToRender.map((block, index) => (
          <BlockWrapper key={index} blockData={block} {...props} />
        ))}
    </>
  );
};
